import React, { useState, useEffect, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useGroupForm } from "../../../hooks/useGroupForm";
import { ActivityPreApprovalsPageSchema } from "../../../Valdations/Schema/group.schema";
import FileUpload from "../../../UploadFile/ActivityPreApprovals";
import styles from "./Pre-Approvals.module.css";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { isViewer } from "../../../interface/RoleHelpers";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/Label";
import { Toaster } from "../../../components/ui/toaster";
import { useToast } from "../../../hooks/use-toast";
import DataTable from "../../../shared/DataTable";
import { Button } from "../../../components/ui/button";
import { ActivityPreApprovalsAxiosInstance } from "../../../Network/ActivityPreApprovals";
import { Datum, ActivityDialogData, CodeOption, WarningDialogState } from "../../../interface/ActivityPreApprovals";
import CodeMultiSelect from "../../../component/CodeMultiSelect";
import ActivityTypeSingleSelector, {
  ActivityType,
  ACTIVITY_TYPE_TO_CODE,
  CODE_TO_ACTIVITY_TYPE,
} from "../../../component/ActivityTypeSelector";
import { BriefcaseMedical, Database, Hash, Layers, MousePointerClick, AlertTriangle } from "lucide-react";
import { canRestoreOrHardDelete } from "../../../interface/RoleHelpers";

const EXPORT_ROW_URL = (id: string | number) => `activity-validation-rules/export/PRE_APPROVAL/${id}`;

const saveBlobResponse = (response: any, fallbackName: string) => {
  const disposition: string = response.headers?.["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error?.message || data?.message || error?.message || fallback;
};

const ActivityPreApprovals: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Configure Levels State
  // =====================================
  const [showLevels, setShowLevels] = useState(false);

  // 1. Doctor
  const [doctorOption, setDoctorOption] = useState<CodeOption[]>([]);
  const [doctorInclusionFlag, setDoctorInclusionFlag] = useState(true);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [isDoctorSelectAll, setIsDoctorSelectAll] = useState(false);

  // 2. Specialty
  const [specialtyOption, setSpecialtyOption] = useState<CodeOption[]>([]);
  const [specialtyInclusionFlag, setSpecialtyInclusionFlag] = useState(true);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [isSpecialtySelectAll, setIsSpecialtySelectAll] = useState(false);

  // 3. TPA
  const [tpaOption, setTpaOption] = useState<CodeOption[]>([]);
  const [tpaInclusionFlag, setTpaInclusionFlag] = useState(true);
  const [tpaMode, setTpaMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isTpaActive, setIsTpaActive] = useState(false);
  const [isTpaSelectAll, setIsTpaSelectAll] = useState(false);

  // 4. Payer
  const [payerOption, setPayerOption] = useState<CodeOption[]>([]);
  const [payerInclusionFlag, setPayerInclusionFlag] = useState(true);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [isPayerSelectAll, setIsPayerSelectAll] = useState(false);

  // 5. Network
  const [networkOption, setNetworkOption] = useState<CodeOption[]>([]);
  const [networkInclusionFlag, setNetworkInclusionFlag] = useState(true);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [isNetworkSelectAll, setIsNetworkSelectAll] = useState(false);

  // 6. Policy
  const [policyOption, setPolicyOption] = useState<CodeOption[]>([]);
  const [policyInclusionFlag, setPolicyInclusionFlag] = useState(true);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [isPolicySelectAll, setIsPolicySelectAll] = useState(false);

  // =====================================
  // Form Input State
  // =====================================
  const [codesByType, setCodesByType] = useState<{
    CPT: CodeOption[];
    HCPCS: CodeOption[];
    Dental: CodeOption[];
    Service: CodeOption[];
  }>({ CPT: [], HCPCS: [], Dental: [], Service: [] });

  const [selectedType, setSelectedType] = useState<ActivityType | undefined>(undefined);
  const [medicalActivityGroups, setMedicalActivityGroups] = useState<CodeOption[]>([]);
  const [customActivityGroups, setCustomActivityGroups] = useState<CodeOption[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Dialog State
  // =====================================
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);

  // =====================================
  // Form Control
  // =====================================
  const [submitted, setSubmitted] = useState(false);
  const { values, errors, setField, validateAll } = useGroupForm(ActivityPreApprovalsPageSchema);

  // =====================================
  // Warning Dialog 
  // =====================================
  type ExtendedWarningDialogState = Omit<WarningDialogState, "type"> & { 
    type: "delete" | "update" | "hardDelete" | null 
  };

  const [warningDialog, setWarningDialog] = useState<ExtendedWarningDialogState>({
    open: false,
    type: null,
    inactiveItems: [],
    id: null,
  });

  // =====================================
  // Selected / Editing State
  // =====================================
  const [selectedActivity, setSelectedActivity] = useState<ActivityDialogData | null>(null);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [editIndex, setEditIndex] = useState<string | number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [ActivityDialogOpen, setActivityDialogOpen] = useState(false);

  // =====================================
  // Search
  // =====================================
  const [advancedSearch, setAdvancedSearch] = useState({
    groupName: "",
    activityCode: "",
    status: "",
    isSoftDeleted: "false",
  });

  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    groupName: "",
    activityCode: "",
    status: "",
    isSoftDeleted: "false",
  });

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim().length < 3) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    if (!lowerText.includes(lowerQuery)) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === lowerQuery ? (
        <span key={index} className="bg-yellow-50 text-yellow-900 px-1 rounded font-semibold">{part}</span>
      ) : part
    );
  };

  const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find((log: any) => log.actorId === item.createdBy);
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };

  // =====================================
  // Toast
  // =====================================
  const { toast } = useToast();

  // =====================================
  // Form Utilities
  // =====================================
  const handleOpenDialog = async (item: any) => {
    try {
      const response = await ActivityPreApprovalsAxiosInstance.get(`activity-validation-rules/${item.id}`);
      if (response.data && response.data.success) {
        const rawData = response.data.data;
        const formattedData = {
          ...rawData,
          codes: rawData.activitySelections
            ?.filter((s: any) => s.activitySelectionType === "SINGLE_CODE")
            .map((s: any) => ({
              code: s.medicalActivityCode.code,
              type:
                s.medicalActivityCode.type === 3 ? "CPT" :
                s.medicalActivityCode.type === 4 ? "HCPCS" :
                s.medicalActivityCode.type === 6 ? "Dental" : "Service",
            })),
          medicalGroups: rawData.activitySelections
            ?.filter((s: any) => s.activitySelectionType === "MEDICAL_GROUP")
            .map((s: any) => ({
              name: s.medicalActivityGroup?.name,
              isActive: s.medicalActivityGroup?.isActive ?? true,
            })),
          customGroups: rawData.activitySelections
            ?.filter((s: any) => s.activitySelectionType === "CUSTOM_GROUP")
            .map((s: any) => ({
              name: s.customActivityGroup?.name || s.customActivityGroup,
              isActive: s.customActivityGroup?.isActive ?? true,
              isSoftDeleted: s.customActivityGroup?.isSoftDeleted ?? false, 
            })),
        };
        setSelectedItem(formattedData);
        setSelectedActivity(formattedData);
        setIsViewDialogOpen(true);
      }
    } catch (error) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    clear();
    setShowAllLogs(false);
  };

  const handleAdd = () => { setEditIndex(null); setIsDialogOpen(true); };

  const clear = () => {
    setSubmitted(false);
    setField("ruleName", "");
    setCodesByType({ CPT: [], HCPCS: [], Dental: [], Service: [] });
    setMedicalActivityGroups([]);
    setCustomActivityGroups([]);
    setIsActive(true);
    setSelectedMainOption(null);
    setSelectedType(undefined);
    setEditIndex(null);
    setShowLevels(false);
    const resetters = [
      { setActive: setIsDoctorActive, setSelectAll: setIsDoctorSelectAll, setOpt: setDoctorOption, setFlag: setDoctorInclusionFlag, setMode: setDoctorMode },
      { setActive: setIsSpecialtyActive, setSelectAll: setIsSpecialtySelectAll, setOpt: setSpecialtyOption, setFlag: setSpecialtyInclusionFlag, setMode: setSpecialtyMode },
      { setActive: setIsTpaActive, setSelectAll: setIsTpaSelectAll, setOpt: setTpaOption, setFlag: setTpaInclusionFlag, setMode: setTpaMode },
      { setActive: setIsPayerActive, setSelectAll: setIsPayerSelectAll, setOpt: setPayerOption, setFlag: setPayerInclusionFlag, setMode: setPayerMode },
      { setActive: setIsNetworkActive, setSelectAll: setIsNetworkSelectAll, setOpt: setNetworkOption, setFlag: setNetworkInclusionFlag, setMode: setNetworkMode },
      { setActive: setIsPolicyActive, setSelectAll: setIsPolicySelectAll, setOpt: setPolicyOption, setFlag: setPolicyInclusionFlag, setMode: setPolicyMode },
    ];
    resetters.forEach(r => {
      r.setActive(false); r.setSelectAll(false); r.setOpt([]);
      if (typeof r.setFlag === "function") r.setFlag(true);
      if (typeof r.setMode === "function") r.setMode("INCLUSION");
    });
  };

  // =====================================
  // Fetch Functions
  // =====================================
  const fetchActivityCodes = useCallback(
    async (
      searchTerm: string,
      page = 1,
      pageSize = 10
    ): Promise<{ data: CodeOption[]; total: number }> => {
      const minCharsRequired = selectedType === "Service" ? 1 : 3;

      if (!searchTerm || searchTerm.trim().length < minCharsRequired || !selectedType) {
        return { data: [], total: 0 };
      }

      const typeCode = ACTIVITY_TYPE_TO_CODE[selectedType as ActivityType];

      try {
        const response = await ActivityPreApprovalsAxiosInstance.get(
          "medical-code/activity/",
          {
            params: { search: searchTerm, page, limit: pageSize, type: typeCode },
          }
        );

        const items: any[] = response.data?.data?.items ?? [];
        const total: number = response.data?.data?.total ?? items.length;

        return {
          data: items.map((item) => ({
            value: item.id,
            label: `${item.code} - ${item.description}`,
          })),
          total,
        };
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });

        return { data: [], total: 0 };
      }
    },
    [selectedType, toast]
  );

  const fetchMedicalCodes = async (searchTerm: string): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1) return { data: [], total: 0 };
    const response = await ActivityPreApprovalsAxiosInstance.get("medical-group/activity/", {
      params: { name: searchTerm, active: true },
    });
    const items = response.data?.data || [];
    return { data: items.map((g: any) => ({ value: g.id, label: g.name })), total: items.length };
  };

  const fetchCustomCodes = async (searchTerm: string): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1) return { data: [], total: 0 };
    const response = await ActivityPreApprovalsAxiosInstance.get(`custom-activity-group/search/${encodeURIComponent(searchTerm)}`);
    const items: string[] = response.data?.data || [];
    const data = items.map(name => ({ value: name, label: name }));
    return { data, total: data.length };
  };

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      ruleType: "PRE_APPROVAL",
    };
    if (advancedSearch.groupName) params.ruleName = advancedSearch.groupName;
    if (advancedSearch.activityCode) params.activityOne = advancedSearch.activityCode;
    if (advancedSearch.status) params.isActive = advancedSearch.status === "Active";
    if (advancedSearch.isSoftDeleted !== "") {
      params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    }
    return params;
  };

  const hasActiveFilters = Boolean(
    advancedSearch.groupName ||
      advancedSearch.activityCode ||
      advancedSearch.status ||
      advancedSearch.isSoftDeleted === "true"
  );

  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await ActivityPreApprovalsAxiosInstance.get("activity-validation-rules", { params });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) setPage(Math.max(1, pages));
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    } 
  };

  // ── Fetch Configure Levels ──────────────────────────────────────
  const fetchDoctorOptions = async (searchTerm: string) => {
    const response = await ActivityPreApprovalsAxiosInstance.get("doctors/names", { params: { query: searchTerm } });
    const items = response.data?.data || [];
    return { data: items.map((i: any) => ({ value: i.id, label: `(${i.licenseNumber}) ${i.name}`, realId: i.id })), total: items.length };
  };
  const fetchTpaOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const r = await ActivityPreApprovalsAxiosInstance.get("tpas/tpa-id", { params: { query: searchTerm } });
      const items = r.data?.data || [];
      return { data: items.map((i: any) => ({ value: i.id, label: i.name || "", realId: i.id })), total: items.length };
    } catch { return { data: [], total: 0 }; }
  };
  const fetchSpecialityOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const r = await ActivityPreApprovalsAxiosInstance.get("specialties/codes", { params: { query: searchTerm } });
      const items = r.data?.data || [];
      return { data: items.map((i: any) => ({ value: i.id, label: i.name || "", realId: i.id })), total: items.length };
    } catch { return { data: [], total: 0 }; }
  };
  const fetchPayerOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const r = await ActivityPreApprovalsAxiosInstance.get("payers/names", { params: { query: searchTerm } });
      const items = r.data?.data || [];
      return { data: items.map((i: any) => ({ value: i.id, label: i.name || "", realId: i.id })), total: items.length };
    } catch { return { data: [], total: 0 }; }
  };
  const fetchPolicyOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const r = await ActivityPreApprovalsAxiosInstance.get("policies/names", { params: { query: searchTerm } });
      const items = r.data?.data || [];
      return { data: items.map((i: any) => ({ value: i.id, label: i.name || "", realId: i.id })), total: items.length };
    } catch { return { data: [], total: 0 }; }
  };
  const fetchNetworkOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const r = await ActivityPreApprovalsAxiosInstance.get("networks/names", { params: { name: searchTerm } });
      const items = r.data?.data || [];
      return { data: items.map((i: any) => ({ value: i.id, label: i.name || "", realId: i.id })), total: items.length };
    } catch { return { data: [], total: 0 }; }
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    const errorData = error.response?.data?.error;
    if (errorData?.details?.length > 0) return errorData.details[0].message;
    if (errorData?.message) return errorData.message;
    if (error.response?.data?.message) return error.response.data.message;
    return error.message || "An unexpected error occurred.";
  };

  // =====================================
  // CRUD
  // =====================================
  const getCodesState = () => {
    if (!selectedType) return { value: [], setter: () => {} };
    return {
      value: codesByType[selectedType] || [],
      setter: (newCodes: CodeOption[]) => setCodesByType(prev => ({ ...prev, [selectedType]: newCodes })),
    };
  };

  const preparePayload = () => {
    const levels: any[] = [];
    const activitySelections: any[] = [];

    const addLevelItems = (options: CodeOption[], type: string, idKey: string, isActive: boolean, isSelectAll: boolean, inclusionFlag: boolean) => {
      if (!isActive) return;
      if (isSelectAll || options.length === 0) {
        levels.push({ exclusionLevel: type, [idKey]: null, inclusionFlag, exclusionOrder: levels.length + 1 });
      } else {
        options.forEach(opt => {
          if (opt.value) levels.push({ exclusionLevel: type, [idKey]: opt.value, inclusionFlag, exclusionOrder: levels.length + 1 });
        });
      }
    };

    addLevelItems(doctorOption, "DOCTOR", "doctorId", isDoctorActive, isDoctorSelectAll, doctorInclusionFlag);
    addLevelItems(specialtyOption, "SPECIALITY", "specialityId", isSpecialtyActive, isSpecialtySelectAll, specialtyInclusionFlag);
    addLevelItems(tpaOption, "TPA", "tpaId", isTpaActive, isTpaSelectAll, tpaInclusionFlag);
    addLevelItems(payerOption, "PAYER", "payerId", isPayerActive, isPayerSelectAll, payerInclusionFlag);
    addLevelItems(networkOption, "NETWORK", "networkId", isNetworkActive, isNetworkSelectAll, networkInclusionFlag);
    addLevelItems(policyOption, "POLICY", "policyId", isPolicyActive, isPolicySelectAll, policyInclusionFlag);

    const seenCodeIds = new Set();
    Object.values(codesByType).forEach((typeArray: any) => {
      typeArray.forEach((c: any) => {
        if (c.value && !seenCodeIds.has(c.value)) {
          seenCodeIds.add(c.value);
          activitySelections.push({ activitySelectionType: "SINGLE_CODE", medicalActivityCodeId: c.value, medicalActivityGroupId: null, customActivityGroup: null });
        }
      });
    });

    medicalActivityGroups.forEach(g => {
      if (g.value) activitySelections.push({ activitySelectionType: "MEDICAL_GROUP", medicalActivityGroupId: g.value });
    });

    customActivityGroups.forEach(cg => {
      if (cg.label) activitySelections.push({ activitySelectionType: "CUSTOM_GROUP", medicalActivityCodeId: null, medicalActivityGroupId: null, customActivityGroup: cg.label });
    });

    return {
      ruleName: values.ruleName.trim(),
      ruleType: "PRE_APPROVAL",
      action: "NOT_ALLOWED",
      priority: (values as any).priority || 1,
      isActive: Boolean(isActive),
      levels,
      activitySelections,
    };
  };

  const addItem = async () => {
    setSubmitted(true);
    if (!validateAll()) return;
    const payload = preparePayload();
    if (payload.activitySelections.length === 0)
      return toast({ title: "Required Field", description: "At least one activity, medical group, or custom group must be selected.", variant: "destructive" });
    const levelValidations = [
      { active: isDoctorActive, selectAll: isDoctorSelectAll, options: doctorOption, label: "Doctor" },
      { active: isTpaActive, selectAll: isTpaSelectAll, options: tpaOption, label: "TPA" },
      { active: isPayerActive, selectAll: isPayerSelectAll, options: payerOption, label: "Payer" },
      { active: isNetworkActive, selectAll: isNetworkSelectAll, options: networkOption, label: "Network" },
      { active: isSpecialtyActive, selectAll: isSpecialtySelectAll, options: specialtyOption, label: "Specialty" },
      { active: isPolicyActive, selectAll: isPolicySelectAll, options: policyOption, label: "Policy" },
    ];
    for (const lvl of levelValidations) {
      if (lvl.active && !lvl.selectAll && lvl.options.length === 0)
        return toast({ title: "Required Field", description: `At least one ${lvl.label} must be selected or select all ${lvl.label}.`, variant: "destructive" });
    }
    try {
      const res = await ActivityPreApprovalsAxiosInstance.post("activity-validation-rules", payload);
      if (res.status === 201 || res.status === 200) {
        fetchItems(); setIsDialogOpen(false); clear();
        toast({ variant: "success", title: "Success", description: "Relation Added Successfully." });
      }
    } catch (error: any) {
      toast({ title: "Validation Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleEdit = async (item: any) => {
    const ruleId = item.id;
    try {
      const response = await ActivityPreApprovalsAxiosInstance.get(`activity-validation-rules/${ruleId}`);
      const fullItem = response.data.data;
      if (!fullItem) return;

      setField("ruleName", fullItem.ruleName || "");
      setIsActive(fullItem.isActive);

      const selections = fullItem.activitySelections || [];

      setMedicalActivityGroups(
        selections.filter((s: any) => s.activitySelectionType === "MEDICAL_GROUP")
          .map((s: any) => ({ value: s.medicalActivityGroup?.id, label: s.medicalActivityGroup?.name, isActive: s.medicalActivityGroup?.isActive }))
      );

      const customGroups = selections
        .filter((s: any) => s.activitySelectionType === "CUSTOM_GROUP")
        .map((s: any) => ({
          value: s.customActivityGroup?.id || s.customActivityGroupId,
          label: s.customActivityGroup?.name,
          isActive: s.customActivityGroup?.isActive,
          isSoftDeleted: s.customActivityGroup?.isSoftDeleted ?? false, 
        }))
        .filter((opt: any) => opt.value);
      setCustomActivityGroups(customGroups);

      const newCodesByType = { CPT: [], HCPCS: [], Dental: [], Service: [] } as any;
      let hasAnyCodes = false;
      let firstFoundType: any = null;
      selections
        .filter((s: any) => s.activitySelectionType === "SINGLE_CODE" || s.activitySelectionType === "SINGLE_ACTIVITY")
        .forEach((s: any) => {
          const code = s.medicalActivityCode;
          if (code) {
            hasAnyCodes = true;
            const typeString = CODE_TO_ACTIVITY_TYPE[String(code.type)] || "CPT";
            newCodesByType[typeString].push({ value: code.id, label: `${code.code} - ${code.description || ""}` });
            if (!firstFoundType) firstFoundType = typeString;
          }
        });
      setCodesByType(newCodesByType);
      if (firstFoundType) setSelectedType(firstFoundType);

      if (hasAnyCodes) setSelectedMainOption("list_of_Activity_code");
      else if (medicalActivityGroups.length > 0) setSelectedMainOption("medical_Activity_groups");
      else if (customGroups.length > 0) setSelectedMainOption("custom_Activity_groups");
      else setSelectedMainOption(null);

      const apiLevels = fullItem.levels || [];
      setShowLevels(apiLevels.length > 0);

      const config = [
        { type: "DOCTOR", setActive: setIsDoctorActive, setSelectAll: setIsDoctorSelectAll, setMode: setDoctorMode, setOption: setDoctorOption, setFlag: setDoctorInclusionFlag, dataKey: "doctor", idField: "doctorId" },
        { type: "SPECIALITY", setActive: setIsSpecialtyActive, setSelectAll: setIsSpecialtySelectAll, setMode: setSpecialtyMode, setOption: setSpecialtyOption, setFlag: setSpecialtyInclusionFlag, dataKey: "speciality", idField: "specialityId" },
        { type: "TPA", setActive: setIsTpaActive, setSelectAll: setIsTpaSelectAll, setMode: setTpaMode, setOption: setTpaOption, setFlag: setTpaInclusionFlag, dataKey: "tpa", idField: "tpaId" },
        { type: "PAYER", setActive: setIsPayerActive, setSelectAll: setIsPayerSelectAll, setMode: setPayerMode, setOption: setPayerOption, setFlag: setPayerInclusionFlag, dataKey: "payer", idField: "payerId" },
        { type: "NETWORK", setActive: setIsNetworkActive, setSelectAll: setIsNetworkSelectAll, setMode: setNetworkMode, setOption: setNetworkOption, setFlag: setNetworkInclusionFlag, dataKey: "network", idField: "networkId" },
        { type: "POLICY", setActive: setIsPolicyActive, setSelectAll: setIsPolicySelectAll, setMode: setPolicyMode, setOption: setPolicyOption, setFlag: setPolicyInclusionFlag, dataKey: "policy", idField: "policyId" },
      ];

      config.forEach(({ type, setActive, setSelectAll, setMode, setOption, setFlag, dataKey, idField }) => {
        const matched = apiLevels.filter((l: any) => l.exclusionLevel === type);
        if (matched.length > 0) {
          setActive(true);
          setFlag(matched[0].inclusionFlag);
          setMode(matched[0].inclusionFlag ? "INCLUSION" : "EXCLUSION");
          const hasAnyId = matched.some((l: any) => l[idField] || l[dataKey]?.id);
          setSelectAll(!hasAnyId);
          if (hasAnyId) {
            setOption(
              matched.map((l: any) => ({
                value: l[idField] || l[dataKey]?.id,
                label: l[dataKey]?.name || l[dataKey]?.policyName || l[dataKey]?.code || "Item",
                isActive: l[dataKey]?.isActive ?? true,
                isSoftDeleted: l[dataKey]?.isSoftDeleted ?? false,
              }))
            );
          }
        } else {
          setActive(false);
        }
      });

      setEditIndex(ruleId);
      setIsDialogOpen(true);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load details", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number, forceUpdate = false) => {
    setSubmitted(true);
    if (!validateAll()) return;

    const hasNoIcdSelections = codesByType.CPT.length === 0 && codesByType.HCPCS.length === 0 && codesByType.Dental.length === 0 && codesByType.Service.length === 0 && medicalActivityGroups.length === 0 && customActivityGroups.length === 0;
    if (hasNoIcdSelections) {
      return toast({ title: "Required Field", description: "At least one activity, medical group, or custom group must be selected.", variant: "destructive" });
    }

    const levelValidations = [
      { active: isDoctorActive, selectAll: isDoctorSelectAll, options: doctorOption, label: "Doctor" },
      { active: isTpaActive, selectAll: isTpaSelectAll, options: tpaOption, label: "TPA" },
      { active: isPayerActive, selectAll: isPayerSelectAll, options: payerOption, label: "Payer" },
      { active: isNetworkActive, selectAll: isNetworkSelectAll, options: networkOption, label: "Network" },
      { active: isSpecialtyActive, selectAll: isSpecialtySelectAll, options: specialtyOption, label: "Specialty" },
      { active: isPolicyActive, selectAll: isPolicySelectAll, options: policyOption, label: "Policy" },
    ];
    for (const lvl of levelValidations) {
      if (lvl.active && !lvl.selectAll && lvl.options.length === 0) {
        return toast({ title: "Required Field", description: `At least one ${lvl.label} must be selected or select all ${lvl.label}.`, variant: "destructive" });
      }
    }

    if (!forceUpdate) {
      const inactiveItems: { label: string; type: string; isSoftDeleted?: boolean }[] = [];

      medicalActivityGroups.forEach((g: any) => {
        if (g.isActive === false) inactiveItems.push({ label: g.label, type: "Medical Group" });
      });

      customActivityGroups.forEach((g: any) => {
        if (g.isActive === false || g.isSoftDeleted === true)
          inactiveItems.push({ label: g.label, type: "Custom Group", isSoftDeleted: g.isSoftDeleted ?? false });
      });

      const levelsCheck = [
        { active: isDoctorActive, options: doctorOption, label: "Doctor" },
        { active: isTpaActive, options: tpaOption, label: "TPA" },
        { active: isPayerActive, options: payerOption, label: "Payer" },
        { active: isSpecialtyActive, options: specialtyOption, label: "Specialty" },
        { active: isNetworkActive, options: networkOption, label: "Network" },
        { active: isPolicyActive, options: policyOption, label: "Policy" },
      ];

      levelsCheck.forEach((lvl) => {
        if (lvl.active) {
          lvl.options.forEach((opt: any) => {
            if (opt.isActive === false || opt.isSoftDeleted === true)
              inactiveItems.push({ label: opt.label, type: lvl.label, isSoftDeleted: opt.isSoftDeleted ?? false });
          });
        }
      });

      if (inactiveItems.length > 0) {
        setWarningDialog({ open: true, type: "update", inactiveItems, id });
        return;
      }
    }

    const payload = preparePayload();
    try {
      const res = await ActivityPreApprovalsAxiosInstance.put(`activity-validation-rules/${id}`, payload);
      if (res.status === 200 || res.status === 204) {
        await fetchItems();
        setIsDialogOpen(false);
        setWarningDialog({ open: false, inactiveItems: [], type: null, id: null });
        clear();
        setEditIndex(null);
        toast({ title: "Success", description: "Relation Updated Successfully.", variant: "success" });
      }
    } catch (error: any) {
      toast({ title: "Validation Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  // ── Delete (soft) ────────────────────────────────────────────────
  const handleDeleteClick = (data: any) => {
    const idToDelete = typeof data === "object" ? data.id || data.ruleId || data._id : data;
    setWarningDialog({ open: true, type: "delete", id: idToDelete, inactiveItems: [] });
  };

  const confirmDelete = async (id: string | number) => {
    try {
      await ActivityPreApprovalsAxiosInstance.delete(`activity-validation-rules/${id}/soft-delete`);
      await fetchItems();
      toast({ title: "Success", description: "Relation deleted successfully.", variant: "success" });
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  // ── Hard Delete ───────────────────────────────────────────────────
  const hardDeleteItem = async (id: string | number) => {
    try {
      await ActivityPreApprovalsAxiosInstance.delete(`activity-validation-rules/${id}`);
      await fetchItems();
      toast({ title: "Success", description: "Relation permanently deleted.", variant: "success" });
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  // ── Restore ───────────────────────────────────────────────────────
  const restoreItem = async (id: string | number) => {
    try {
      await ActivityPreApprovalsAxiosInstance.patch(`activity-validation-rules/${id}/restore`);
      await fetchItems();
      toast({ title: "Success", description: "Relation restored successfully.", variant: "success" });
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const cleanSelectionsArray = (selections: any[]) =>
    (selections || []).map((s: any) => ({
      activitySelectionType: s.activitySelectionType,
      medicalActivityCodeId: s.medicalActivityCode?.id || s.medicalActivityCodeId || null,
      medicalActivityGroupId: s.medicalActivityGroup?.id || s.medicalActivityGroupId || null,
      customActivityGroup: typeof s.customActivityGroup === "object"
        ? (s.customActivityGroup?.name || s.customActivityGroup?.label)
        : (s.customActivityGroup || null),
    }));

  const cleanLevelsArray = (levels: any[]) =>
    (levels || []).map((l: any) => ({
      exclusionLevel: l.exclusionLevel,
      inclusionFlag: l.inclusionFlag,
      exclusionOrder: l.exclusionOrder,
      networkId: l.networkId || l.network?.id || null,
      policyId: l.policyId || l.policy?.id || null,
      specialityId: l.specialityId || l.speciality?.id || null,
      doctorId: l.doctorId || l.doctor?.id || null,
      payerId: l.payerId || l.payer?.id || null,
      tpaId: l.tpaId || l.tpa?.id || null,
    }));

  const toggleActive = async (row: Datum) => {
    if (row.isSoftDeleted) return; 
    try {
      const response = await ActivityPreApprovalsAxiosInstance.get(`activity-validation-rules/${row.id}`);
      const fullData = response.data.data;
      if (!fullData) return;
      const updatedStatus = !row.isActive;
      const payload = {
        ...fullData,
        isActive: updatedStatus,
        activitySelections: cleanSelectionsArray(fullData.activitySelections || []),
        levels: cleanLevelsArray(fullData.levels || []),
      };
      await ActivityPreApprovalsAxiosInstance.put(`activity-validation-rules/${row.id}`, payload);
      setAllItems((prev) => prev.map((item) => (item.id === row.id ? { ...item, isActive: updatedStatus } : item)));
      toast({ variant: "success", title: "Success", description: `Status updated to ${updatedStatus ? "active" : "inactive"}` });
    } catch (error: any) {
      toast({ title: "Error", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  // =====================================
  // Effects
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAdvancedSearch({
        groupName: advancedSearchInput.groupName.trim(),
        activityCode: advancedSearchInput.activityCode.trim(),
        status: advancedSearchInput.status,
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, advancedSearch]);

  // =====================================
  // Download Function (Backend Export)
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await ActivityPreApprovalsAxiosInstance.get(EXPORT_ROW_URL(item.id), {
        responseType: "blob",
      });
      saveBlobResponse(response, `ActivityPreApprovals_${item.ruleName || "export"}.xlsx`);
      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.ruleName}" exported successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: await getBlobErrorMessage(error, "Failed to export relation."),
        variant: "destructive",
      });
    }
  };

  // =====================================
  // Table Columns
  // =====================================
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "ruleName",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full font-bold text-black justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`Relation-name-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("ruleName")}
          </div>
        );
      },
    },

    {
      id: "activityCode",
      header: () => <div className="w-full font-bold text-black justify-center flex normal-case">Activities</div>,
      accessorFn: (row: any) => {
        const selections = row.activitySelections || [];
        const typePriority: Record<string, number> = { SINGLE_CODE: 1, SINGLE_ACTIVITY: 1, MEDICAL_GROUP: 2, CUSTOM_GROUP: 3 };
        return selections.map((sel: any) => {
          let label = "", type = sel.activitySelectionType, isActive = true, codeType = null, isSoftDeleted = false;
          if (type === "SINGLE_CODE" || type === "SINGLE_ACTIVITY") {
            label = sel.medicalActivityCode?.code || sel.activityCode || "";
            isActive = sel.medicalActivityCode?.isActive ?? true;
            codeType = sel.medicalActivityCode?.type;
          } else if (type === "MEDICAL_GROUP") {
            label = sel.medicalActivityGroup?.name || "";
            isActive = sel.medicalActivityGroup?.isActive ?? true;
          } else if (type === "CUSTOM_GROUP") {
            label = sel.customActivityGroup?.name || sel.customActivityGroup || "";
            isActive = sel.customActivityGroup?.isActive ?? true;
            isSoftDeleted = sel.customActivityGroup?.isSoftDeleted ?? false; 
          }
          return { label, type, isActive, codeType, isSoftDeleted };
        }).filter((i: any) => i.label)
          .sort((a: any, b: any) => (typePriority[a.type] || 99) - (typePriority[b.type] || 99));
      },
      cell: ({ row }) => {
        const activityData = (row.getValue("activityCode") as any[]) || [];
        const allSelections = row.original.activitySelections || [];
        const item = row.original;

        if (activityData.length === 0)
          return <span className="text-slate-300 text-center block font-medium">—</span>;

        const activeSearch = advancedSearch?.activityCode?.toLowerCase().trim() || search?.toLowerCase().trim() || "";
        const filteredActivitys = activeSearch ? activityData.filter(i => i.label.toLowerCase().includes(activeSearch)) : activityData;
        const displayActivitys = filteredActivitys.slice(0, 2);
        const remainingCount = filteredActivitys.length - 2;

        const typeStyles: Record<string, string> = {
          SINGLE_CODE: "bg-indigo-50 border-indigo-100 text-indigo-600 ring-1 ring-indigo-200/50",
          SINGLE_ACTIVITY: "bg-indigo-50 border-indigo-100 text-indigo-600 ring-1 ring-indigo-200/50",
          MEDICAL_GROUP: "bg-emerald-50 border-emerald-100 text-emerald-600 ring-1 ring-emerald-200/50",
          CUSTOM_GROUP: "bg-violet-50 border-violet-100 text-violet-600 ring-1 ring-violet-200/50",
        };

        return (
          <div className={`flex flex-col items-center gap-1.5 py-1 ${item.isSoftDeleted ? "opacity-40" : ""}`}>
            <div className="flex flex-wrap justify-center items-center gap-2">
              {displayActivitys.map((itm, idx) => {
                const isMatch = activeSearch && itm.label.toLowerCase().includes(activeSearch);
                const typeLabel = itm.codeType ? (CODE_TO_ACTIVITY_TYPE[String(itm.codeType)] || "") : "";
                const baseStyle = itm.isSoftDeleted
                  ? "opacity-35 blur-[0.4px] line-through border-red-200 bg-red-50 text-red-500"
                  : itm.isActive === false
                  ? "bg-gray-50 border-gray-200 text-gray-400 opacity-60 grayscale"
                  : (typeStyles[itm.type] || "bg-slate-50 border-slate-200 text-slate-600");

                return (
                  <div key={idx}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 border shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${isMatch ? "bg-amber-50 border-amber-400 text-amber-900 ring-2 ring-amber-200 scale-105" : baseStyle}`}
                    title={itm.isSoftDeleted ? "Deleted Item" : itm.isActive === false ? "Inactive" : itm.label}
                  >
                    <span className="flex items-center justify-center">
                      {itm.isSoftDeleted
                        ? <i className="fa-solid fa-trash-can text-[9px] opacity-70"></i>
                        : itm.type.includes("GROUP")
                        ? <i className="fa-solid fa-layer-group text-[9px] opacity-70"></i>
                        : <i className="fa-solid fa-tag text-[9px] opacity-70"></i>}
                    </span>
                    <span className={`text-[11px] font-semibold tracking-tight flex items-center gap-1 ${itm.isActive === false && !itm.isSoftDeleted ? "line-through decoration-gray-300" : ""}`}>
                      {typeLabel && <span className="text-[9px] opacity-60 font-bold">({typeLabel})</span>}
                      {highlightMatch(itm.label, activeSearch)}
                    </span>
                    {!itm.isSoftDeleted && itm.isActive === false && <i className="fa-solid fa-eye-slash text-[8px] opacity-60 ml-0.5"></i>}
                  </div>
                );
              })}

              {remainingCount > 0 && (
                <button
                  data-testid={`btn-view-more-activities-${item.id}`}
                  onClick={() => {
                    setSelectedActivity({
                      codes: allSelections.filter((s: any) => s.activitySelectionType === "SINGLE_CODE" || s.activitySelectionType === "SINGLE_ACTIVITY")
                        .map((s: any) => ({ code: s.medicalActivityCode?.code || s.activityCode || "", type: CODE_TO_ACTIVITY_TYPE[String(s.medicalActivityCode?.type)] || "CPT", isActive: s.medicalActivityCode?.isActive ?? true })),
                      medicalGroups: allSelections.filter((s: any) => s.activitySelectionType === "MEDICAL_GROUP")
                        .map((s: any) => ({ name: s.medicalActivityGroup?.name || "", isActive: s.medicalActivityGroup?.isActive ?? true })),
                      customGroups: allSelections.filter((s: any) => s.activitySelectionType === "CUSTOM_GROUP")
                        .map((s: any) => ({
                          name: s.customActivityGroup?.name || s.customActivityGroup || "",
                          isActive: s.customActivityGroup?.isActive ?? true,
                          isSoftDeleted: s.customActivityGroup?.isSoftDeleted ?? false,
                        })),
                    });
                    setActivityDialogOpen(true);
                  }}
                  className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold bg-white border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white transition-all shadow-sm"
                >
                  <i className="fa-solid fa-plus mr-1 text-[8px]"></i>
                  {remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "isActive",
      header: () => <div className="w-full font-bold text-black text-center normal-case">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center">
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.isActive ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.isActive ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },

    {
      id: "view",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full font-bold text-black text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          View{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              data-testid={`btn-view-${item.id}`}
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  // =====================================
  // RENDER
  // =====================================
  return (
    <div data-testid="ActivityPreApprovals-page">
      <div className={styles.container} data-testid="ActivityPreApprovals-page-container">
        <h5 className={styles.title} data-testid="ActivityPreApprovals-page-title">
          <span className={styles.dialogIcon} data-testid="ActivityPreApprovals-page-title-icon">
            <i className="fa-solid fa-file-shield"></i>
          </span>
          Activity Pre-Approvals
        </h5>
        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* ── Search ── */}
      <div className={`${styles["compact-search-container"]} ${styles["mb-6"]}`} data-testid="ActivityPreApprovals-page-search-container">
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]}>
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>
            <button
              data-testid="ActivityPreApprovals-page-btn-reset-filters"
              onClick={() => setAdvancedSearchInput({ groupName: "", activityCode: "", status: "", isSoftDeleted: "false" })}
              className={styles["btn-clear-compact"]}
            >
              <i className="fa-solid fa-rotate-left"></i> Reset
            </button>
          </div>

          <div className={styles["search-fields-compact"]} style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            {/* Relation Name */}
            <div className={styles["field-compact"]} data-testid="ActivityPreApprovals-page-search-groupname">
              <label className={styles["label-compact"]}><i className="fa-solid fa-layer-group"></i> Relation Name</label>
              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="ActivityPreApprovals-page-input-search-groupname"
                  type="text"
                  placeholder="Search relations..."
                  value={advancedSearchInput.groupName}
                  onChange={e => setAdvancedSearchInput({ ...advancedSearchInput, groupName: e.target.value })}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Activity Code */}
            <div className={styles["field-compact"]} data-testid="ActivityPreApprovals-page-search-activitycode">
              <label className={styles["label-compact"]}><i className="fa-solid fa-code"></i> Activities</label>
              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="ActivityPreApprovals-page-input-search-activitycode"
                  type="text"
                  placeholder="Search Activities..."
                  value={advancedSearchInput.activityCode}
                  onChange={e => setAdvancedSearchInput({ ...advancedSearchInput, activityCode: e.target.value })}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Status */}
            <div className={styles["field-compact"]} data-testid="ActivityPreApprovals-page-search-status">
              <label className={styles["label-compact"]}><i className="fa-solid fa-toggle-on"></i> Status</label>
              <div className={styles["select-container-compact"]}>
                <select
                  data-testid="ActivityPreApprovals-page-select-search-status"
                  value={advancedSearchInput.status}
                  onChange={e => setAdvancedSearchInput({ ...advancedSearchInput, status: e.target.value })}
                  className={styles["input-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Deletion Status */}
            <div
              className={styles["field-compact"]}
              data-testid="customIcd-page-search-soft-deleted"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted Relations
              </label>
              <button
                data-testid="customIcd-page-btn-toggle-soft-deleted"
                type="button"
                onClick={() =>
                  setAdvancedSearchInput({
                    ...advancedSearchInput,
                    isSoftDeleted: advancedSearchInput.isSoftDeleted === "true" ? "false" : "true",
                  })
                }
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i
                  className={`fa-solid ${
                    advancedSearchInput.isSoftDeleted === "true" ? "fa-rotate-left" : "fa-eye-slash"
                  } text-xs`}
                ></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true" ? "Back to List" : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]}>
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]} data-testid="ActivityPreApprovals-page-stats">
              Showing <strong>{allItems.length}</strong> of <strong>{totalItems}</strong> relations
            </span>
            {(advancedSearchInput.groupName || advancedSearchInput.activityCode || advancedSearchInput.status || advancedSearchInput.isSoftDeleted === "true") && (
              <span className={styles["badge-filtered-compact"]}><i className="fa-solid fa-filter"></i> Filtered</span>
            )}
          </div>
        </div>
      </div>

      {/* ── View Dialog ── */}
      {isViewDialogOpen && selectedItem && (
        <dialog className={styles["modern-dialog"]} open data-testid="ActivityPreApprovals-page-dialog">
          <div className={styles["dialog-container"]}>
            <div className={styles["dialog-header"]}>
              <div className={styles["header-content"]}>
                <div className={styles["header-icon"]}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="2" />
                    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles["dialog-title"]}>Relation Details</h2>
                  <p className={styles["dialog-subtitle"]}>{selectedItem.ruleName}</p>
                </div>
              </div>
              <button data-testid="ActivityPreApprovals-dialog-btn-close-top" className={styles["close-btn"]} onClick={handleCloseDialog}>✕</button>
            </div>

            <div className={styles["dialog-body"]}>
              {/* Basic Info */}
              <section className={styles.section} data-testid="ActivityPreApprovals-page-dialog-basic-info">
                <h3 className={styles["section-title"]}><i className="fa-solid fa-circle-info text-blue-500 mr-2"></i> Basic Information</h3>
                <div className={styles.infoGrid}>
                  <div className={`${styles.infoBox} col-span-2`} data-testid="ActivityPreApprovals-dialog-relation-name">
                    <span className={styles.compactLabel}>Relation Name</span>
                    <span className={styles.compactValue}>{selectedItem.ruleName}</span>
                  </div>

                  {/* Medical Groups */}
                  <div className={`${styles.infoBox} col-span-1`} data-testid="ActivityPreApprovals-dialog-medical-groups">
                    <span className={styles.compactLabel}>Medical Activity Groups</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedItem.activitySelections?.filter(s => s.activitySelectionType === "MEDICAL_GROUP").map((sel, i) => {
                        const isInactive = sel.medicalActivityGroup?.isActive === false;
                        return (
                          <span key={i} className={`${styles.miniChip} ${isInactive ? "opacity-40 grayscale bg-gray-200" : ""}`} title={isInactive ? "Inactive Group" : ""}>
                            {sel.medicalActivityGroup?.name || "N/A"}
                            {isInactive && <i className="fa-solid fa-eye-slash ml-1 text-[8px]"></i>}
                          </span>
                        );
                      }) || <span className="text-gray-400 text-xs">—</span>}
                    </div>
                  </div>

                  {/* Custom Groups */}
                  <div className={`${styles.infoBox} col-span-1`} data-testid="ActivityPreApprovals-dialog-custom-groups">
                    <span className={styles.compactLabel}>Custom Activity Groups</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedItem.activitySelections?.filter(s => s.activitySelectionType === "CUSTOM_GROUP").map((sel, i) => {
                        const isDeleted = sel.customActivityGroup?.isSoftDeleted === true;
                        const isInactive = sel.customActivityGroup?.isActive === false;
                        const groupName = typeof sel.customActivityGroup === "object" ? sel.customActivityGroup?.name : sel.customActivityGroup;
                        return (
                          <span key={i}
                            className={`${styles.miniChipSecondary} ${isDeleted ? "opacity-35 blur-[0.3px] line-through border-dashed border-red-200 bg-red-50 text-red-500" : isInactive ? "opacity-40 grayscale bg-gray-200" : ""}`}
                            title={isDeleted ? "Deleted Group" : isInactive ? "Inactive Group" : ""}
                          >
                            {isDeleted
                              ? <i className="fa-solid fa-trash-can mr-1 text-[8px] text-red-500"></i>
                              : isInactive
                              ? <i className="fa-solid fa-eye-slash mr-1 text-[8px]"></i>
                              : null}
                            {groupName || "N/A"}
                            {isDeleted && <span className="text-[8px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1">Deleted</span>}
                          </span>
                        );
                      }) || <span className="text-gray-400 text-xs">—</span>}
                    </div>
                  </div>
                </div>
              </section>

              {/* Activity Codes By Type */}
              <section className={styles.codesSection} data-testid="ActivityPreApprovals-dialog-codes-section">
                <h3 className={styles.sectionTitleView}><i className="fa-solid fa-code"></i> Activity Codes By Type</h3>
                <div className={styles.typesContainer}>
                  {["CPT", "HCPCS", "Dental", "Service"].map(type => {
                    const filteredCodes = (selectedActivity?.codes as any[])?.filter(c => c.type === type);
                    return filteredCodes?.length > 0 && (
                      <div key={type} className={styles.typeGroup} data-testid={`activity-type-group-${type.toLowerCase()}`}>
                        <div className={`${styles.typeHeader} ${styles[type.toLowerCase()]}`}>
                          <i className="fa-solid fa-stethoscope"></i>
                          <span className={styles.typeName}>{type}</span>
                          <span className={styles.typeCount}>{filteredCodes.length}</span>
                        </div>
                        <div className={styles.codesGridView}>
                          {filteredCodes.map((item, idx) => <span key={idx} className={styles.codePill}>{item.code}</span>)}
                        </div>
                      </div>
                    );
                  })}
                  {(!selectedActivity?.codes || (selectedActivity?.codes as any[]).length === 0) && (
                    <div className={styles.emptyStateContainer}>
                      <div className={styles.emptyStateIllustration}><i className="fa-solid fa-folder-open text-slate-200 text-3xl"></i></div>
                      <div className={styles.emptyStateContent}><h5 className="text-slate-500 font-bold text-[11px] tracking-wider">No individual codes</h5></div>
                    </div>
                  )}
                </div>
              </section>

              {/* Configure Level Details */}
              <section className={styles.section} data-testid="ActivityPreApprovals-page-dialog-levels">
                <h3 className={styles["section-title"]}>
                  <i className="fa-solid fa-layer-group text-blue-500 mr-2"></i> Configure Level Details
                </h3>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {(
                    [
                      { label: "Doctor", icon: "fa-user-doctor", key: "DOCTOR", path: "doctor", nameKey: "name", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", chip: "bg-blue-200 text-blue-800" },
                      { label: "TPA", icon: "fa-file-invoice", key: "TPA", path: "tpa", nameKey: "name", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", chip: "bg-amber-200 text-amber-800" },
                      { label: "Payer", icon: "fa-wallet", key: "PAYER", path: "payer", nameKey: "name", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", chip: "bg-emerald-200 text-emerald-800" },
                      { label: "Network", icon: "fa-network-wired", key: "NETWORK", path: "network", nameKey: "name", bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", chip: "bg-purple-200 text-purple-800" },
                      { label: "Specialty", icon: "fa-microscope", key: "SPECIALITY", path: "speciality", nameKey: "name", bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", chip: "bg-rose-200 text-rose-800" },
                      { label: "Policy", icon: "fa-shield-halved", key: "POLICY", path: "policy", nameKey: "policyName", bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", chip: "bg-teal-200 text-teal-800" },
                    ] as const
                  ).map((lvl) => {
                    const matchingLevels = selectedItem.levels?.filter((l) => l.exclusionLevel === lvl.key) || [];
                    const isConfigured = matchingLevels.length > 0;
                    const isInclusion = isConfigured ? matchingLevels[0].inclusionFlag : false;

                    return (
                      <div key={lvl.key} data-testid={`view-dialog-level-${lvl.key.toLowerCase()}`} className={`rounded-xl border-2 transition-all ${isConfigured ? `bg-white ${lvl.border} shadow-sm` : "bg-gray-50 border-gray-100 opacity-50"}`}>
                        <div className={`px-3 py-1.5 flex items-center justify-between ${isConfigured ? lvl.bg : "bg-gray-100"}`}>
                          <span className={`flex items-center gap-1.5 text-[10px] font-black ${isConfigured ? lvl.text : "text-gray-500"}`}>
                            <i className={`fa-solid ${lvl.icon} text-[12px]`}></i> {lvl.label}
                          </span>
                          {isConfigured && (
                            <div className="flex gap-1.5 items-center">
                              <span className={`text-[9px] px-2 py-0.5 rounded-md font-bold flex items-center gap-1 shadow-sm ${isInclusion ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                                <i className={`fa-solid ${isInclusion ? "fa-circle-check" : "fa-circle-xmark"}`}></i>
                                {isInclusion ? "Inclusion" : "Exclusion"}
                              </span>
                              <span className={`${lvl.chip} text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-current opacity-80`}>
                                {matchingLevels.length} ON
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          {isConfigured ? (
                            <div className="flex flex-wrap gap-1">
                              {matchingLevels.map((l, index) => {
                                const entity = (l as any)[lvl.path];
                                const name = entity ? entity[lvl.nameKey] : "All Enrolled";
                                const isInactive = entity && entity.isActive === false;
                                const isDeleted = entity && entity.isSoftDeleted === true;

                                return (
                                  <span
                                    key={index}
                                    className={`font-bold text-xs px-2 py-0.5 rounded border transition-colors flex items-center gap-1
                                      ${isDeleted
                                        ? "bg-red-50 text-red-400 border-red-200 opacity-50 line-through decoration-red-400 border-dashed"
                                        : isInactive
                                        ? "bg-gray-100 text-gray-400 border-gray-200 opacity-50 grayscale"
                                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-white"
                                      }`}
                                    title={isDeleted ? "Deleted Item" : isInactive ? "Inactive Item" : ""}
                                  >
                                    {isDeleted
                                      ? <i className="fa-solid fa-trash-can text-[9px] text-red-400 opacity-70"></i>
                                      : isInactive
                                      ? <i className="fa-solid fa-eye-slash text-[9px] opacity-70"></i>
                                      : null}
                                    {name}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-[11px] italic">Not configured</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ── Audit Trail / Logs ── */}
              <div style={{ padding: "20px 28px 8px" }} data-testid="ActivityPreApprovals-page-dialog-audit-trail">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{getCreatorName(selectedItem) || "—"}</span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdAt
                          ? new Date((selectedItem as any).createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedAt
                          ? new Date((selectedItem as any).updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem as any).auditLog
                        .slice(0, showAllLogs ? undefined : 4)
                        .map((log: any, i: number) => (
                          <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                                {String(log.actorName ?? "?")[0].toUpperCase()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                              {i === 0 && (
                                <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                                  Latest
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                              {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        ))}

                      {(selectedItem as any).auditLog.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button
                            data-testid="ActivityPreApprovals-dialog-btn-toggle-logs"
                            onClick={() => setShowAllLogs((v) => !v)}
                            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
                          >
                            {showAllLogs ? "Show less ↑" : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["dialog-footer"]}>
              <button data-testid="ActivityPreApprovals-dialog-btn-close-bottom" className={styles["btn-secondary"]} onClick={handleCloseDialog}>Close</button>
            </div>
          </div>
        </dialog>
      )}

      {/* ── Activity Breakdown Dialog (View More) ── */}
      <Dialog open={ActivityDialogOpen} onOpenChange={setActivityDialogOpen} data-testid="ActivityPreApprovals-page-activity-dialog">
        <DialogContent className={styles.dialogContent}>
          <header className={styles.header}>
            <div className="flex items-center gap-4">
              <div className={styles.headerIconBox}><i className="fa-solid fa-list-check text-white text-lg"></i></div>
              <div><DialogTitle className={styles.title}>Selected activities breakdown</DialogTitle><p className={styles.subtitle}>Detailed View</p></div>
            </div>
          </header>
          <div className={styles.body}>
            {/* 1. Individual Codes */}
            <section className={styles.codesSection} data-testid="ActivityPreApprovals-dialog-codes-section">
              <h3 className={styles.sectionTitleView}><i className="fa-solid fa-code"></i> Activity Codes By Type</h3>
              <div className={styles.typesContainer}>
                {["CPT", "HCPCS", "Dental", "Service"].map(type => {
                  const filteredCodes = (selectedActivity?.codes as any[])?.filter(c => c.type === type);
                  return filteredCodes?.length > 0 && (
                    <div key={type} className={styles.typeGroup} data-testid={`activity-type-group-${type.toLowerCase()}`}>
                      <div className={`${styles.typeHeader} ${styles[type.toLowerCase()]}`}>
                        <i className="fa-solid fa-stethoscope"></i>
                        <span className={styles.typeName}>{type}</span>
                        <span className={styles.typeCount}>{filteredCodes.length}</span>
                      </div>
                      <div className={styles.codesGridView}>
                        {filteredCodes.map((item, idx) => <span key={idx} className={styles.codePill}>{item.code}</span>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 2. Medical Groups */}
            {(selectedActivity?.medicalGroups?.length ?? 0) > 0 && (
              <section className={`${styles.section} ${styles.sectionPurple}`} data-testid="ActivityPreApprovals-dialog-medical-groups-section">
                <h4 className={styles.sectionTitle}>Medical Groups</h4>
                <div className="space-y-2">
                  {selectedActivity?.medicalGroups?.map((group: any, idx: number) => (
                    <div key={idx} className={`${styles.groupItem} ${styles.medicalItem} ${group.isActive === false ? "opacity-60 bg-slate-50 border-dashed" : ""}`}>
                      <i className={`fa-solid ${group.isActive !== false ? "fa-stethoscopes text-purple-500" : "fa-eye-slash text-slate-400"} text-xs`}></i>
                      <span className={`${styles.groupText} ${group.isActive === false ? "line-through text-slate-400" : ""}`}>{group.name}</span>
                      {group.isActive === false && <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Inactive</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 3. Custom Groups */}
            {(selectedActivity?.customGroups?.length ?? 0) > 0 && (
              <section className={`${styles.section} ${styles.sectionEmerald}`} data-testid="ActivityPreApprovals-dialog-custom-groups-section">
                <h4 className={styles.sectionTitle}>Custom Groups</h4>
                <div className="space-y-2">
                  {selectedActivity?.customGroups?.map((group: any, idx: number) => {
                    const isDeleted = group.isSoftDeleted === true;
                    return (
                      <div key={idx} className={`${styles.groupItem} ${styles.customItem} ${isDeleted ? "opacity-40 blur-[0.3px] line-through border-dashed border-red-200 bg-red-50/40" : group.isActive === false ? "opacity-60 bg-slate-50 border-dashed" : ""}`} title={isDeleted ? "Deleted Group" : group.isActive === false ? "Inactive Group" : ""}>
                        <i className={`fa-solid ${isDeleted ? "fa-trash-can text-red-500" : group.isActive !== false ? "fa-folder-plus text-emerald-500" : "fa-eye-slash text-slate-400"} text-xs`}></i>
                        <span className={`${styles.groupText} ${isDeleted || group.isActive === false ? "line-through text-slate-400" : ""}`}>{group.name}</span>
                        {isDeleted
                          ? <span className="ml-auto text-[9px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full border border-red-200 flex items-center gap-1"><i className="fa-solid fa-trash-can text-[8px]"></i>Deleted</span>
                          : group.isActive === false
                          ? <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Inactive</span>
                          : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
          <footer className={styles.footer}>
            <span className={styles.footerBrand}>Qudra</span>
            <Button data-testid="ActivityPreApprovals-dialog-breakdown-btn-close" onClick={() => setActivityDialogOpen(false)} className="bg-slate-900 text-white px-8 rounded-xl font-bold text-xs">Close</Button>
          </footer>
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={open => { if (!open) { clear(); setEditIndex(null); } setIsDialogOpen(open); }}>
        <DialogContent className={styles.dialogContent} data-testid="ActivityPreApprovals-page-dialog-content">
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle} data-testid="ActivityPreApprovals-form-dialog-title">
              {editIndex === null ? "Add Activity Pre-Approvals Relation" : "Edit Activity Pre-Approvals Relation"}
            </DialogTitle>
            <DialogDescription className={styles.dialogDescription}>Fill in the details below to {editIndex === null ? "create" : "update"} an Activity Pre-Approvals Relation.</DialogDescription>
          </DialogHeader>

          <div className={styles.dialogBody} data-testid="ActivityPreApprovals-page-dialog-body">
            <div className="flex flex-col gap-6">
              <div className={`${styles.formGroup} bg-slate-50/50 p-4 rounded-xl border border-slate-100`} data-testid="ActivityPreApprovals-page-input-groupname">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name" className="text-xs font-bold text-slate-700 ml-1">Relation Name :</Label>
                  <Input
                    data-testid="ActivityPreApprovals-form-input-relationname"
                    placeholder="Enter Name..."
                    value={values.ruleName}
                    onChange={e => setField("ruleName", e.target.value)}
                    className="bg-white"
                  />
                  {submitted && errors.ruleName && <span className={styles.error} data-testid="ActivityPreApprovals-form-error-relationName">{errors.ruleName}</span>}
                </div>
              </div>

              <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm h-auto" data-testid="ActivityPreApprovals-form-section-activity-config">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <div>
                        <Label className="text-xs font-bold text-slate-800 block">Activity Configuration</Label>
                        <span className="text-[9px] text-slate-400 font-medium">Manage relation activities and groups</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1 p-0.5 bg-slate-100/80 rounded-lg">
                    {[
                      { id: "list_of_Activity_code", label: "Codes", icon: Hash },
                      { id: "medical_Activity_groups", label: "Medical", icon: BriefcaseMedical },
                      { id: "custom_Activity_groups", label: "Custom", icon: Layers },
                    ].map(tab => (
                      <button
                        data-testid={`ActivityPreApprovals-tab-${tab.id}`}
                        key={tab.id}
                        type="button"
                        onClick={() => setSelectedMainOption(tab.id)}
                        className={`flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-bold transition-all
                          ${selectedMainOption === tab.id ? "bg-white text-blue-600 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"}`}
                      >
                        <tab.icon className="w-3 h-3" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="h-auto min-h-0">
                    {selectedMainOption ? (
                      <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                        {selectedMainOption === "list_of_Activity_code" ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1" data-testid="ActivityPreApprovals-section-activity-type-selector">
                              <Label className="text-[9px] font-bold text-slate-400 ml-1">Activity Type</Label>
                              <ActivityTypeSingleSelector value={selectedType} onChange={setSelectedType} />
                            </div>
                            {selectedType && (
                              <div className="space-y-1 animate-in fade-in duration-200" data-testid="ActivityPreApprovals-section-activity-codes">
                                <Label className="text-[9px] font-bold text-slate-400 ml-1">Select Codes</Label>
                                <CodeMultiSelect fetchOptions={fetchActivityCodes} value={getCodesState().value} onChange={getCodesState().setter} placeholder="Search codes..."  minChars={selectedType === "Service" ? 1 : 3}/>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1" data-testid="ActivityPreApprovals-section-activity-groups">
                            <Label className="text-[9px] font-bold text-slate-400 ml-1">Search Groups</Label>
                            <CodeMultiSelect
                              fetchOptions={selectedMainOption === "medical_Activity_groups" ? fetchMedicalCodes : fetchCustomCodes}
                              value={selectedMainOption === "medical_Activity_groups" ? medicalActivityGroups : customActivityGroups}
                              onChange={selectedMainOption === "medical_Activity_groups" ? setMedicalActivityGroups : setCustomActivityGroups}
                              placeholder="Search groups..."
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 py-3 px-4 border-2 border-dashed border-slate-100 rounded-lg bg-slate-50/30">
                        <MousePointerClick className="w-4 h-4 text-slate-300" />
                        <p className="text-[11px] font-bold text-slate-500 italic">Choose how you want to add activities to this relation</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Configure Levels */}
          <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-6" data-testid="ActivityPreApprovals-form-section-configure-levels">
            <div className="flex items-center justify-between border-b border-blue-200 pb-4">
              <div className="flex items-center gap-2 text-blue-900">
                <i className="fa-solid fa-layer-group text-lg"></i>
                <h2 className="text-base font-bold tracking-tight">Configure Levels</h2>
              </div>
              <button
                data-testid="ActivityPreApprovals-toggle-configure-levels"
                onClick={() => setShowLevels(!showLevels)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showLevels ? "bg-green-500" : "bg-gray-300"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showLevels ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            {showLevels && (
              <div className="grid grid-cols-2 gap-4">
                {/* Doctor */}
                <div data-testid="level-config-doctor" className={`bg-white rounded-xl border p-4 transition-all relative group ${isDoctorActive ? "border-blue-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isDoctorActive && (
                    <button data-testid="btn-clear-doctor-level" onClick={() => { setIsDoctorActive(false); setDoctorMode(null); setDoctorOption([]); setIsDoctorSelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isDoctorActive ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-user-doctor text-[12px]"></i> Doctor
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-doctor-mode-exclusion"
                        onClick={() => { if (doctorMode !== "EXCLUSION") { setDoctorOption([]); setIsDoctorSelectAll(false); } setDoctorMode("EXCLUSION"); setDoctorInclusionFlag(false); setIsDoctorActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isDoctorActive && doctorMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-doctor-mode-inclusion"
                        onClick={() => { if (doctorMode !== "INCLUSION") { setDoctorOption([]); setIsDoctorSelectAll(false); } setDoctorMode("INCLUSION"); setDoctorInclusionFlag(true); setIsDoctorActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isDoctorActive && doctorMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isDoctorActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-blue-50/30 p-2.5 rounded-lg border border-dashed border-blue-100">
                        <input data-testid="checkbox-doctor-select-all" type="checkbox" checked={isDoctorSelectAll} onChange={e => { setIsDoctorSelectAll(e.target.checked); if (e.target.checked) setDoctorOption([]); }} className="w-4 h-4 rounded-sm accent-blue-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-blue-900/70">Select all doctors</span>
                      </div>
                      {!isDoctorSelectAll && <CodeMultiSelect placeholder="Search Doctors..." value={doctorOption} onChange={setDoctorOption} fetchOptions={fetchDoctorOptions} />}
                    </div>
                  )}
                </div>

                {/* Specialty */}
                <div data-testid="level-config-specialty" className={`bg-white rounded-xl border p-4 transition-all relative group ${isSpecialtyActive ? "border-rose-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isSpecialtyActive && (
                    <button data-testid="btn-clear-specialty-level" onClick={() => { setIsSpecialtyActive(false); setSpecialtyMode(null); setSpecialtyOption([]); setIsSpecialtySelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isSpecialtyActive ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-microscope text-[12px]"></i> Specialty
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-specialty-mode-exclusion"
                        onClick={() => { if (specialtyMode !== "EXCLUSION") { setSpecialtyOption([]); setIsSpecialtySelectAll(false); } setSpecialtyMode("EXCLUSION"); setSpecialtyInclusionFlag(false); setIsSpecialtyActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isSpecialtyActive && specialtyMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-specialty-mode-inclusion"
                        onClick={() => { if (specialtyMode !== "INCLUSION") { setSpecialtyOption([]); setIsSpecialtySelectAll(false); } setSpecialtyMode("INCLUSION"); setSpecialtyInclusionFlag(true); setIsSpecialtyActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isSpecialtyActive && specialtyMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isSpecialtyActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-rose-50/30 p-2.5 rounded-lg border border-dashed border-rose-100">
                        <input data-testid="checkbox-specialty-select-all" type="checkbox" checked={isSpecialtySelectAll} onChange={e => { setIsSpecialtySelectAll(e.target.checked); if (e.target.checked) setSpecialtyOption([]); }} className="w-4 h-4 rounded-sm accent-rose-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-rose-900/70">Select all specialties</span>
                      </div>
                      {!isSpecialtySelectAll && <CodeMultiSelect placeholder="Search..." value={specialtyOption} onChange={setSpecialtyOption} fetchOptions={fetchSpecialityOptions} />}
                    </div>
                  )}
                </div>

                {/* TPA */}
                <div data-testid="level-config-tpa" className={`bg-white rounded-xl border p-4 transition-all relative group ${isTpaActive ? "border-amber-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isTpaActive && (
                    <button data-testid="btn-clear-tpa-level" onClick={() => { setIsTpaActive(false); setTpaMode(null); setTpaOption([]); setIsTpaSelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isTpaActive ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-file-invoice text-[12px]"></i> TPA
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-tpa-mode-exclusion"
                        onClick={() => { if (tpaMode !== "EXCLUSION") { setTpaOption([]); setIsTpaSelectAll(false); } setTpaMode("EXCLUSION"); setTpaInclusionFlag(false); setIsTpaActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isTpaActive && tpaMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-tpa-mode-inclusion"
                        onClick={() => { if (tpaMode !== "INCLUSION") { setTpaOption([]); setIsTpaSelectAll(false); } setTpaMode("INCLUSION"); setTpaInclusionFlag(true); setIsTpaActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isTpaActive && tpaMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isTpaActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-amber-50/30 p-2.5 rounded-lg border border-dashed border-amber-100">
                        <input data-testid="checkbox-tpa-select-all" type="checkbox" checked={isTpaSelectAll} onChange={e => { setIsTpaSelectAll(e.target.checked); if (e.target.checked) setTpaOption([]); }} className="w-4 h-4 rounded-sm accent-amber-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-amber-900/70">Select all TPAs</span>
                      </div>
                      {!isTpaSelectAll && <CodeMultiSelect placeholder="Search..." value={tpaOption} onChange={setTpaOption} fetchOptions={fetchTpaOptions} />}
                    </div>
                  )}
                </div>

                {/* Payer */}
                <div data-testid="level-config-payer" className={`bg-white rounded-xl border p-4 transition-all relative group ${isPayerActive ? "border-emerald-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isPayerActive && (
                    <button data-testid="btn-clear-payer-level" onClick={() => { setIsPayerActive(false); setPayerMode(null); setPayerOption([]); setIsPayerSelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isPayerActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-wallet text-[12px]"></i> Payer
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-payer-mode-exclusion"
                        onClick={() => { if (payerMode !== "EXCLUSION") { setPayerOption([]); setIsPayerSelectAll(false); } setPayerMode("EXCLUSION"); setPayerInclusionFlag(false); setIsPayerActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isPayerActive && payerMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-payer-mode-inclusion"
                        onClick={() => { if (payerMode !== "INCLUSION") { setPayerOption([]); setIsPayerSelectAll(false); } setPayerMode("INCLUSION"); setPayerInclusionFlag(true); setIsPayerActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isPayerActive && payerMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isPayerActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-emerald-50/30 p-2.5 rounded-lg border border-dashed border-emerald-100">
                        <input data-testid="checkbox-payer-select-all" type="checkbox" checked={isPayerSelectAll} onChange={e => { setIsPayerSelectAll(e.target.checked); if (e.target.checked) setPayerOption([]); }} className="w-4 h-4 rounded-sm accent-emerald-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-emerald-900/70">Select all payers</span>
                      </div>
                      {!isPayerSelectAll && <CodeMultiSelect placeholder="Search..." value={payerOption} onChange={setPayerOption} fetchOptions={fetchPayerOptions} />}
                    </div>
                  )}
                </div>

                {/* Network */}
                <div data-testid="level-config-network" className={`bg-white rounded-xl border p-4 transition-all relative group ${isNetworkActive ? "border-purple-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isNetworkActive && (
                    <button data-testid="btn-clear-network-level" onClick={() => { setIsNetworkActive(false); setNetworkMode(null); setNetworkOption([]); setIsNetworkSelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isNetworkActive ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-network-wired text-[12px]"></i> Network
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-network-mode-exclusion"
                        onClick={() => { if (networkMode !== "EXCLUSION") { setNetworkOption([]); setIsNetworkSelectAll(false); } setNetworkMode("EXCLUSION"); setNetworkInclusionFlag(false); setIsNetworkActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isNetworkActive && networkMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-network-mode-inclusion"
                        onClick={() => { if (networkMode !== "INCLUSION") { setNetworkOption([]); setIsNetworkSelectAll(false); } setNetworkMode("INCLUSION"); setNetworkInclusionFlag(true); setIsNetworkActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isNetworkActive && networkMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isNetworkActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-purple-50/30 p-2.5 rounded-lg border border-dashed border-purple-100">
                        <input data-testid="checkbox-network-select-all" type="checkbox" checked={isNetworkSelectAll} onChange={e => { setIsNetworkSelectAll(e.target.checked); if (e.target.checked) setNetworkOption([]); }} className="w-4 h-4 rounded-sm accent-purple-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-purple-900/70">Select all networks</span>
                      </div>
                      {!isNetworkSelectAll && <CodeMultiSelect placeholder="Search..." value={networkOption} onChange={setNetworkOption} fetchOptions={fetchNetworkOptions} />}
                    </div>
                  )}
                </div>

                {/* Policy */}
                <div data-testid="level-config-policy" className={`bg-white rounded-xl border p-4 transition-all relative group ${isPolicyActive ? "border-teal-200 shadow-sm" : "border-slate-100 opacity-80"}`}>
                  {isPolicyActive && (
                    <button data-testid="btn-clear-policy-level" onClick={() => { setIsPolicyActive(false); setPolicyMode(null); setPolicyOption([]); setIsPolicySelectAll(false); }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10">
                      <i className="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  )}
                  <div className="flex flex-col items-center gap-4">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isPolicyActive ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      <i className="fa-solid fa-shield-halved text-[12px]"></i> Policy
                    </span>
                    <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
                      <button
                        data-testid="btn-policy-mode-exclusion"
                        onClick={() => { if (policyMode !== "EXCLUSION") { setPolicyOption([]); setIsPolicySelectAll(false); } setPolicyMode("EXCLUSION"); setPolicyInclusionFlag(false); setIsPolicyActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isPolicyActive && policyMode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Exclusion
                      </button>
                      <button
                        data-testid="btn-policy-mode-inclusion"
                        onClick={() => { if (policyMode !== "INCLUSION") { setPolicyOption([]); setIsPolicySelectAll(false); } setPolicyMode("INCLUSION"); setPolicyInclusionFlag(true); setIsPolicyActive(true); }}
                        className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isPolicyActive && policyMode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
                      >
                        Inclusion
                      </button>
                    </div>
                  </div>
                  {isPolicyActive && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-3 bg-teal-50/30 p-2.5 rounded-lg border border-dashed border-teal-100">
                        <input data-testid="checkbox-policy-select-all" type="checkbox" checked={isPolicySelectAll} onChange={e => { setIsPolicySelectAll(e.target.checked); if (e.target.checked) setPolicyOption([]); }} className="w-4 h-4 rounded-sm accent-teal-600 cursor-pointer shadow-sm" />
                        <span className="text-[12px] font-bold text-teal-900/70">Select all policies</span>
                      </div>
                      {!isPolicySelectAll && <CodeMultiSelect placeholder="Search..." value={policyOption} onChange={setPolicyOption} fetchOptions={fetchPolicyOptions} />}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className={styles.dialogFooter}>
            <Button data-testid="ActivityPreApprovals-form-btn-submit" onClick={() => { editIndex === null ? addItem() : updateItem(editIndex); }} className="bg-blue-600 hover:bg-blue-700">
              {editIndex === null ? "Add Relation" : "Update Relation"}
            </Button>
            <Button data-testid="ActivityPreApprovals-form-btn-cancel" className={styles.cancel} variant="outline" onClick={() => { setIsDialogOpen(false); setEditIndex(null); setSubmitted(false); clear(); }}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Warning Dialog ── */}
      <Dialog open={warningDialog.open} onOpenChange={open => !open && setWarningDialog(prev => ({ ...prev, open: false }))} data-testid="ActivityPreApprovals-page-warning-dialog">
        <DialogContent className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col border-none shadow-2xl">
          <div className="p-6 pb-2">
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 font-bold ${warningDialog.type === "delete" || warningDialog.type === "hardDelete" ? "text-red-600" : "text-amber-600"}`}>
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "delete" ? "Confirm Deletion" : warningDialog.type === "hardDelete" ? "Permanent Deletion" : "Inactive Items Detected"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-sm mt-2">
                {warningDialog.type === "delete"
                  ? "Are you sure you want to delete this relation? This action cannot be undone."
                  : warningDialog.type === "hardDelete"
                  ? "This will permanently remove the relation from the system. This action cannot be undone."
                  : "The following selected items are currently inactive or deleted. Do you still want to proceed?"}
              </DialogDescription>
            </DialogHeader>
          </div>

          {warningDialog.type === "hardDelete" && (
            <div className="px-6 py-6 flex flex-col items-center justify-center gap-3">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
              </div>
              <p className="text-sm text-slate-600 text-center px-4">This relation will be <strong className="text-slate-800">permanently deleted</strong>. This cannot be undone.</p>
            </div>
          )}

          {warningDialog.type === "update" && warningDialog.inactiveItems.length > 0 && (
            <div className="px-6 py-2 max-h-[250px] overflow-y-auto space-y-2 my-2" data-testid="warning-dialog-inactive-items-list">
              {warningDialog.inactiveItems.map((item: any, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-slate-400 tracking-wider">{item.type}</span>
                    <span className={`text-sm font-bold ${item.isSoftDeleted ? "line-through text-slate-400" : "text-slate-700"}`}>{item.label}</span>
                  </div>
                  {item.isSoftDeleted ? (
                    <div className="flex items-center gap-1.5 text-red-500 bg-red-50 px-2 py-1 rounded-md border border-red-100">
                      <i className="fa-solid fa-trash-can text-[10px]"></i>
                      <span className="text-[10px] font-bold">Deleted</span>
                    </div>
                  ) : (
                    <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">Inactive</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="p-6 pt-4 bg-slate-50/50 border-t flex gap-3">
            <Button data-testid="warning-dialog-btn-cancel" variant="outline" className="flex-1 rounded-xl h-11 font-bold text-slate-600 border-slate-200"
              onClick={() => setWarningDialog({ open: false, inactiveItems: [], id: null, type: null })}>
              Cancel
            </Button>
            <button
              data-testid="warning-dialog-btn-confirm"
              className={`flex-1 rounded-xl h-11 text-white font-black text-sm transition-all shadow-lg ${
                warningDialog.type === "delete" || warningDialog.type === "hardDelete"
                  ? "bg-red-500 hover:bg-red-600 shadow-red-200"
                  : "bg-amber-500 hover:bg-amber-600 shadow-amber-200"
              }`}
              onClick={() => {
                if (warningDialog.id !== null) {
                  if (warningDialog.type === "delete") confirmDelete(warningDialog.id!);
                  else if (warningDialog.type === "hardDelete") hardDeleteItem(warningDialog.id!);
                  else if (warningDialog.type === "update") updateItem(warningDialog.id!, true);
                }
                setWarningDialog({ open: false, inactiveItems: [], id: null, type: null });
              }}
            >
              {warningDialog.type === "delete" ? "Yes, Delete" : warningDialog.type === "hardDelete" ? "Yes, Permanently Delete" : "Yes, Update anyway"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster data-testid="ActivityPreApprovals-page-toaster" />

      <div data-testid="ActivityPreApprovals-page-table-wrapper">
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => { if (item.isSoftDeleted) return; handleEdit(item); }}
          onDelete={(data: any) => {
            const item = typeof data === "object" ? data : allItems.find(i => i.id === data);
            if (item?.isSoftDeleted) return;
            handleDeleteClick(data);
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New Relation"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={newPage => setPage(newPage + 1)}
          onPageSizeChange={size => { setLimit(size); setPage(1); }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg">
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div className="flex items-center justify-center gap-1.5">
                <button data-testid={`btn-restore-${item.id}`} onClick={() => restoreItem(item.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all">
                  <i className="fa-solid fa-rotate-left text-[9px]"></i> Restore
                </button>
                <button data-testid={`btn-hard-delete-${item.id}`} onClick={() => setWarningDialog({ open: true, type: "hardDelete", id: item.id, inactiveItems: [] })}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all">
                  <i className="fa-solid fa-trash text-[9px]"></i> Delete
                </button>
              </div>
            );
          }}
          data-testid="ActivityPreApprovals-page-datatable"
        />
      </div>
    </div>
  );
};

export default ActivityPreApprovals;